import { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { convertThemeDataToCssVariables, DEFAULT_THEME, useSettings, useThemeConfig } from '@/entities/settings';
import { Spinner } from '@ibsys/kit';

/**
 * Контекст для передачи данных темы компонентам
 */
export interface ThemeContextValue {
  projectName: string;
  logoSvg: string;
  favicon: string;
}

const ThemeContext = createContext<ThemeContextValue>({
  projectName: '',
  logoSvg: '',
  favicon: '',
});

/**
 * Хук для доступа к данным темы из контекста
 */
export const useTheme = (): ThemeContextValue => {
  return useContext(ThemeContext);
};

/**
 * Provider компонент для применения темы из theme-config (ProjectConfigResponse).
 *
 * - Получает конфигурацию через useThemeConfig (themes — массив, colorParameter с code в snake_case)
 * - Выбирает тему по коду (light/dark) из useSettings
 * - Преобразует code (snake_case) в CSS-переменные (--kebab-case) через themeKeyToCssVar
 * - Применяет переменные на :root, обновляет document.title и favicon
 */
export const ThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data: projectConfig, isLoading } = useThemeConfig();
  const { theme: currentTheme } = useSettings();

  const currentThemeData = useMemo(() => {
    if (!projectConfig?.themes?.length) return null;
    const found = projectConfig.themes.find((t) => t.code === currentTheme);
    return found ?? projectConfig.themes.find((t) => t.code === DEFAULT_THEME) ?? projectConfig.themes[0];
  }, [projectConfig?.themes, currentTheme]);

  const contextValue: ThemeContextValue = useMemo(
    () => ({
      projectName: projectConfig?.projectName ?? '',
      logoSvg: currentThemeData?.logoSvg ?? '',
      favicon: projectConfig?.favicon ?? '',
    }),
    [currentThemeData?.logoSvg, projectConfig?.projectName, projectConfig?.favicon],
  );

  const cssVariables = useMemo(() => {
    if (!currentThemeData) return {};
    return convertThemeDataToCssVariables(currentThemeData);
  }, [currentThemeData]);

  useEffect(() => {
    if (!currentThemeData) return;

    const root = document.documentElement;
    Object.entries(cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    return () => {
      Object.keys(cssVariables).forEach((key) => {
        root.style.removeProperty(key);
      });
    };
  }, [cssVariables]);

  useEffect(() => {
    if (projectConfig?.projectName) {
      document.title = projectConfig.projectName;
    }
  }, [projectConfig?.projectName]);

  useEffect(() => {
    if (!projectConfig?.favicon) return;

    // Находим существующий favicon или создаем новый
    let faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

    if (!faviconLink) {
      faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      document.head.appendChild(faviconLink);
    }

    // Извлекаем MIME-тип из data URI для корректного отображения
    const dataUriMatch = projectConfig.favicon.match(/^data:(image\/[^;,]+)/);
    if (dataUriMatch) {
      faviconLink.type = dataUriMatch[1];
    }

    faviconLink.href = projectConfig.favicon;
  }, [projectConfig?.favicon]);

  if (isLoading) {
    return <Spinner className={'fixed top-1/2 left-1/2 -translate-1/2 size-9'} />;
  }

  // Показываем children даже во время загрузки, чтобы не блокировать рендер приложения
  // Конфигурация темы применится как только данные загрузятся
  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};
